import NotFound from "@/components/Common/NotFound";
import type {TokenClaimsSerializer} from "@/generated/DjangoTypes";
import {i18n} from "@/i18n";
import PendingAmbassadors from "@/pages/PendingAmbassadors";
import {InfluencerSidebarStoreProvider} from "@/store/influencerSidebarContext";
import useTokenStore from "@/store/token";
import {fetchCompany} from "@/utils/loaderUtils";
import {createFileRoute, notFound, redirect} from "@tanstack/react-router";
import {jwtDecode} from "jwt-decode";

export const Route = createFileRoute("/_authenticated/_company/company/$companyId/pending-ambassadors")({
  component: RouteComponent,
  notFoundComponent: () => (
    <NotFound message={i18n.t("groups:company_not_found")} page={i18n.t("common:dashboard")} redirect="/" />
  ),
  params: {
    parse: raw => ({companyId: +raw.companyId}),
    stringify: params => ({companyId: params.companyId?.toString()}),
  },
  loader: async ({params}) => {
    const token = useTokenStore.getState().access;

    if (!token) {
      throw redirect({to: "/login", replace: true});
    }

    const claims = jwtDecode<TokenClaimsSerializer>(token);
    const isAdmin = claims.access_level === "COMPANY_ADMIN" || claims.access_level === "INFLUENCER_ADMIN";
    const isAgency = claims.access_level === "AGENCY";

    if (!isAdmin && !isAgency) {
      if (!claims.sub_brand_ids?.includes(params.companyId)) {
        throw notFound();
      }

      return;
    }

    const company = await fetchCompany(params.companyId).catch(() => null);

    if (!company) {
      throw notFound();
    }

    if (company.agency?.multiple_brands) {
      throw redirect({
        to: "/agency/$agencyId/subbrand/$companyId/pending-ambassadors",
        params: {agencyId: company.agency.id, companyId: company.id},
        replace: true,
      });
    }
  },
});

function RouteComponent() {
  const {companyId} = Route.useParams();

  return (
    <InfluencerSidebarStoreProvider>
      <PendingAmbassadors companyId={companyId} />
    </InfluencerSidebarStoreProvider>
  );
}
